/**
 * vipProgramAdmin.service.ts — Admin > Patients/Leads > V.I.P. Program
 * (legacy admin_user_tags.php): the medal thresholds of the account's three
 * V.I.P. tags (tags type 'admin': 1 patients referred, 2 money spent in the
 * practice, 3 money spent by referrals), each with a bronze, silver, gold
 * and platinum tier (tag_details min / max). Platinum has no upper end.
 * Reads GET /tags?type=admin; saves a tier with PUT
 * /tags/{tagID}/tag-details/{id} (Admin Access).
 */
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';

export type VipTierName = 'bronze' | 'silver' | 'gold' | 'platinum';

export const VIP_TIERS: VipTierName[] = ['bronze', 'silver', 'gold', 'platinum'];

export interface VipTier {
  id: number;
  tagId: number;
  name: VipTierName;
  min: number;
  /** None for platinum. */
  max: number | null;
}

export interface VipTag {
  id: number;
  title: string;
  /** 1 referrals (a count), 2 / 3 money. */
  typeId: number;
  tiers: VipTier[];
}

/** typeID 2 and 3 are dollar amounts. */
export function isMoneyTag(tag: Pick<VipTag, 'typeId'>): boolean {
  return tag.typeId === 2 || tag.typeId === 3;
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function tierName(v: unknown): VipTierName | null {
  const n = String(v ?? '').trim().toLowerCase();
  return (VIP_TIERS as string[]).includes(n) ? (n as VipTierName) : null;
}

export function listVipTags(): Promise<VipTag[]> {
  return apiClient.get<ApiResponse<Record<string, unknown>[]>>('/tags', { params: { type: 'admin' } }).then((r) =>
    (r.data.data ?? [])
      .map((t): VipTag => {
        const details = Array.isArray(t.tagDetails) ? (t.tagDetails as Record<string, unknown>[]) : [];
        const tiers = details
          .map((d): VipTier | null => {
            const name = tierName(d.name);
            if (!name) return null;
            const max = d.maxValue == null || d.maxValue === '' ? null : num(d.maxValue);
            // Platinum's "0" is legacy's empty upper end.
            return { id: num(d.tagDetailsID), tagId: num(t.tagID), name, min: num(d.minValue), max: name === 'platinum' ? null : max };
          })
          .filter((d): d is VipTier => d !== null)
          .sort((a, b) => VIP_TIERS.indexOf(a.name) - VIP_TIERS.indexOf(b.name));
        return { id: num(t.tagID), title: String(t.title ?? ''), typeId: num(t.typeID), tiers };
      })
      .filter((t) => t.tiers.length > 0)
      .sort((a, b) => a.typeId - b.typeId || a.id - b.id),
  );
}

/** Saves a tier's range; platinum sends no upper end. */
export function saveVipTier(tier: Pick<VipTier, 'id' | 'tagId' | 'name'>, min: number, max: number | null): Promise<void> {
  const body: Record<string, number> = { minValue: min };
  if (tier.name !== 'platinum' && max !== null) body.maxValue = max;
  return apiClient.put(`/tags/${tier.tagId}/tag-details/${tier.id}`, body).then(() => undefined);
}

export interface VipRangeIssue {
  tierId: number;
  field: 'min' | 'max';
  level: 'error' | 'warning';
  message: string;
}

/**
 * Legacy's checks on a tag's ranges (as numbers; null = empty): a value must be
 * a whole number of 0 or more, From below To (platinum has no To), and a tier
 * starting at or below the previous tier's To overlaps it — legacy marked
 * that red; here it is a warning, since the highest tier reached still wins.
 */
export function vipRangeIssues(tiers: { id: number; name: VipTierName; min: number | null; max: number | null }[]): VipRangeIssue[] {
  const issues: VipRangeIssue[] = [];
  tiers.forEach((t, i) => {
    const minBad = t.min === null || t.min < 0;
    if (t.min === null) issues.push({ tierId: t.id, field: 'min', level: 'error', message: 'Enter where this tier starts' });
    else if (t.min < 0) issues.push({ tierId: t.id, field: 'min', level: 'error', message: "Can't be below 0" });
    if (t.name !== 'platinum') {
      if (t.max === null) issues.push({ tierId: t.id, field: 'max', level: 'error', message: 'Enter where this tier ends' });
      else if (t.max < 0) issues.push({ tierId: t.id, field: 'max', level: 'error', message: "Can't be below 0" });
      else if (t.min !== null && t.min >= t.max) issues.push({ tierId: t.id, field: 'max', level: 'error', message: 'Must be above From' });
    }
    const prev = tiers[i - 1];
    if (!minBad && prev && prev.max !== null && t.min !== null && t.min <= prev.max) {
      issues.push({ tierId: t.id, field: 'min', level: 'warning', message: `Overlaps ${prev.name}, which ends at ${prev.max}` });
    }
  });
  return issues;
}
